/**
 * Merchant knowledge (TMMPDM-0112; tmm-api 0194 U2/U3, 0195 U1): the words and small rules the
 * patterns queue, the unlisted banks and the naming-quality report share.
 *
 * The composition rule: tmm-api serves the verdict (YAML at `f7c843fb`): each pattern's
 * `promotable` and the list's `promotable=true|false` filter. The candidates split into Ready for
 * review and Still short on that filter alone; the console never decides readiness from the six gates,
 * which stay detail, as sent. "n of 6" is a tally of those results, shown as text; it splits, filters,
 * sorts and enables nothing.
 */
import {
  ApiError,
  errorCopy,
  merchantPatternFailedGates,
  merchantPatternState,
} from '@/api/errors';
import {
  INSTITUTION_KINDS,
  MERCHANT_PATTERN_KINDS,
  type MerchantPatternApproveRequest,
  type MerchantPatternGate,
  type MerchantPatternKind,
  type MerchantPatternRow,
  type MerchantPatternState,
} from '@/api/tmm/types';
import { NOT_SENT, formatList } from '@/format';

export type Translate = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

/** The page's tabs (URL `tab`): one topic each. */
export const KNOWLEDGE_TABS = ['patterns', 'banks'] as const;
export type KnowledgeTab = (typeof KNOWLEDGE_TABS)[number];

/** The patterns' queue tabs (URL `view`): the two candidate tabs differ only by the server's filter. */
export const PATTERN_VIEWS = ['ready', 'short', 'approved', 'retired'] as const;
export type PatternView = (typeof PATTERN_VIEWS)[number];

/** Each tab's query: a state and, for the candidates, the server's `promotable` filter. */
export const VIEW_QUERY: Record<
  PatternView,
  { state: MerchantPatternState; promotable?: boolean }
> = {
  ready: { state: 'CANDIDATE', promotable: true },
  short: { state: 'CANDIDATE', promotable: false },
  approved: { state: 'APPROVED' },
  retired: { state: 'RETIRED' },
};

export type PatternAction = 'approve' | 'reject' | 'retire';

/**
 * Each tab's actions follow the server's filter that filled it; no row field is read. Still short
 * offers no Approve…: its candidates fail a gate (MM-D-151), possibly NOT_PERSONAL_NAME or REDACTED, so
 * they may name a person or carry unredacted text, and are rejected or left to gather evidence.
 */
export const VIEW_ACTIONS: Record<PatternView, readonly PatternAction[]> = {
  ready: ['approve', 'reject'],
  short: ['reject'],
  approved: ['retire'],
  retired: [],
};

/** The 404 codes a deployed merchant-knowledge route answers: what it couldn't find. */
const NOT_FOUND_CODES: ReadonlySet<string> = new Set([
  'MERCHANT_PATTERN_NOT_FOUND',
  'INSTITUTION_REQUEST_NOT_FOUND',
]);

/**
 * A 404 that names nothing it couldn't find: tmm-api hasn't deployed the route yet (an unmapped route
 * answers `NOT_FOUND`, or no code). A coded 404 is a deployed route's answer, worded by its code.
 */
export function isNotDeployed(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 404 && !NOT_FOUND_CODES.has(String(error.code))
  );
}

/** A failed action in words: not deployed, the code's copy, or the dialog's own line. */
export function actionError(error: unknown, t: Translate, thing: string, fallback: string): string {
  if (isNotDeployed(error)) return notAvailable(t, thing);
  return errorCopy(error, t, fallback);
}

const NOT_AVAILABLE_FALLBACK: Record<string, string> = {
  patterns: "Merchant patterns aren't available yet.",
  banks: "Unlisted banks aren't available yet.",
  naming: "Naming quality isn't available yet.",
};

/** "{{Thing}} isn't available yet." — neutral, with no retry. */
export function notAvailable(t: Translate, thing: string): string {
  return t(`knowledge.notAvailable.${thing}`, NOT_AVAILABLE_FALLBACK[thing] ?? thing);
}

const GATE_FALLBACK: Record<string, string> = {
  NOT_P2P: 'Not a person-to-person payment',
  MOSTLY_BUSINESS: 'Mostly confirmed as a business',
  MERCHANT_MARKERS: 'Merchant markers',
  NOT_PERSONAL_NAME: 'Not a personal name',
  THRESHOLD: 'Threshold met',
  REDACTED: 'Redacted',
};

/** A gate by name; one this console doesn't know shows its code. */
export function gateLabel(gate: string, t: Translate): string {
  return Object.hasOwn(GATE_FALLBACK, gate)
    ? t(`knowledge.gate.${gate}`, GATE_FALLBACK[gate])
    : gate;
}

/** A gate's result in words, as sent. */
export function gateResult(passed: boolean, t: Translate): string {
  return passed
    ? t('knowledge.gateResult.passed', 'Passed')
    : t('knowledge.gateResult.notPassed', 'Not passed');
}

/** "4 of 6": the results as sent, tallied for display. Not a verdict. */
export function gatesTally(gates: readonly MerchantPatternGate[], t: Translate): string {
  return t('knowledge.gatesTally', '{{passed, number}} of {{total, number}}', {
    passed: gates.filter((gate) => gate.passed === true).length,
    total: gates.length,
  });
}

const KIND_FALLBACK: Record<string, string> = {
  VENDOR: 'Business',
  OTHER: 'Other',
  PERSON: 'Person',
};

/** A payee kind; one this console doesn't know shows its code, and none chosen (`null`) is "—". */
export function kindLabel(kind: string | null, t: Translate): string {
  if (kind === null) return NOT_SENT;
  return Object.hasOwn(KIND_FALLBACK, kind)
    ? t(`knowledge.kind.${kind}`, KIND_FALLBACK[kind])
    : kind;
}

/** The kinds an approval may choose (never `PERSON`). */
export const APPROVE_KINDS = MERCHANT_PATTERN_KINDS;

const INSTITUTION_KIND_FALLBACK: Record<string, string> = {
  BANK: 'Bank',
  CREDIT_UNION: 'Credit union',
  OTHER: 'Other',
};

export function institutionKindLabel(kind: string, t: Translate): string {
  return Object.hasOwn(INSTITUTION_KIND_FALLBACK, kind)
    ? t(`knowledge.institutionKind.${kind}`, INSTITUTION_KIND_FALLBACK[kind])
    : kind;
}

export { INSTITUTION_KINDS };

const SOURCE_FALLBACK: Record<string, string> = {
  KNOWN_MERCHANT: 'Known merchants',
  BANK_TEXT: 'Bank text',
  AI: 'AI',
};

/** A naming source (`NamingQualitySourceRow.source`); one this console doesn't know shows its code. */
export function sourceLabel(source: string, t: Translate): string {
  return Object.hasOwn(SOURCE_FALLBACK, source)
    ? t(`knowledge.source.${source}`, SOURCE_FALLBACK[source])
    : source;
}

/** Reject's reason: 1-500 characters once trimmed (`MerchantPatternRejectRequest`). */
export const REASON_MAX = 500;
/** Approve's name: 1-120 characters once trimmed (`MerchantPatternApproveRequest`). */
export const NAME_MAX = 120;
/** Add to catalog's name (1-200) and city (at most 100), once trimmed (`InstitutionRequestAdd`). */
export const INSTITUTION_NAME_MAX = 200;
export const CITY_MAX = 100;

/** A pattern's proposed name; `""` (every name members chose was withheld) is said in words. */
export function patternName(row: Pick<MerchantPatternRow, 'proposedName'>, t: Translate): string {
  return row.proposedName === ''
    ? t('knowledge.noProposedName', 'No name proposed')
    : row.proposedName;
}

const STATE_NOW_FALLBACK: Record<MerchantPatternState, string> = {
  CANDIDATE: "This pattern is a candidate now, so that can't be done.",
  APPROVED: "This pattern is approved now, so that can't be done.",
  RETIRED: "This pattern is retired now, so that can't be done.",
};

/**
 * A refused pattern action in words, inside its dialog (TMM-B4):
 * - 409 `MERCHANT_PATTERN_NOT_PROMOTABLE {failedGates}`: the failed gates, in the checklist's words;
 * - 409 `MERCHANT_PATTERN_STATE {state}`: the state it has now, as a sentence;
 * - otherwise not deployed, the code's copy, or the dialog's own line (`actionError`).
 * A refusal sent without its detail falls to the code's generic copy.
 */
export function patternActionError(
  error: unknown,
  t: Translate,
  locale: string,
  fallback: string,
): string {
  const failedGates = merchantPatternFailedGates(error);
  if (failedGates !== null && failedGates.length > 0) {
    return t(
      'knowledge.approve.notPromotable',
      "This pattern can't be approved yet. It doesn't pass: {{gates}}.",
      {
        gates: formatList(
          failedGates.map((gate) => gateLabel(gate, t)),
          locale,
        ),
      },
    );
  }
  const state = merchantPatternState(error);
  if (state !== null && Object.hasOwn(STATE_NOW_FALLBACK, state)) {
    return t(`knowledge.stateNow.${state}`, STATE_NOW_FALLBACK[state as MerchantPatternState]);
  }
  return actionError(error, t, 'patterns', fallback);
}

/** The refusals that mean the row moved on (gone, handled, or its gates or state changed). */
const STALE_CODES: ReadonlySet<string> = new Set([
  'MERCHANT_PATTERN_NOT_FOUND',
  'MERCHANT_PATTERN_NOT_PROMOTABLE',
  'MERCHANT_PATTERN_STATE',
  'INSTITUTION_REQUEST_NOT_FOUND',
  'INSTITUTION_REQUEST_STATE',
]);

/** A refusal after which the list behind the dialog is read again; the dialog keeps its words. */
export function isStaleRow(error: unknown): boolean {
  return error instanceof ApiError && STALE_CODES.has(String(error.code));
}

/** The approve body: `name` and `kind` only when changed from the proposed ones. */
export function approveBody(
  row: Pick<MerchantPatternRow, 'proposedName' | 'kind'>,
  name: string,
  kind: MerchantPatternKind,
): MerchantPatternApproveRequest {
  const trimmed = name.trim();
  return {
    ...(trimmed !== row.proposedName ? { name: trimmed } : {}),
    ...(kind !== row.kind ? { kind } : {}),
  };
}
